import LoadingOverlay from 'components/common/LoadingOverlay';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Table from 'rc-table';
import { useIntl, type IntlShape } from 'react-intl';
import { get, isArray, isNull, omit, pick } from 'lodash';
import { formatValue } from 'utils/helpers';
import useGetCO2Calculations from 'hooks/co2-calculator/useCO2Calculations';
import type { CO2CalculationsQuery } from 'graphql/generated/graphql';
import requireValue from 'utils/requireValue';

type TFormatMessage = IntlShape['formatMessage'];

type TCO2Calculations = NonNullable<CO2CalculationsQuery['co2Calculations']>;

type TCO2FormValues = TCO2Calculations['co2_emissions_before']['form_values'];

type TCO2Result = TCO2Calculations['co2_emissions_before']['result'];

/** The scenarios' form values picked by path (a scenario is missing when it is null). */
interface ICO2InputParameters {
  co2_emissions_before?: { form_values: TCO2FormValues };
  co2_emissions_potential?: { form_values: TCO2FormValues };
  co2_emissions_after?: { form_values: TCO2FormValues } | null;
}

/** The scenarios' results picked by path (a scenario is missing when it is null). */
interface ICO2OutputParameters {
  co2_emissions_before?: { result: TCO2Result };
  co2_emissions_potential?: { result: TCO2Result };
  co2_emissions_after?: { result: TCO2Result } | null;
}

interface IParametersTableProps<TValues> {
  values: TValues;
  t: TFormatMessage;
}

/** A form value or a result of a scenario looked up by the keys of the first scenario. */
const valueOf = (values: object, key: string): unknown => get(values, key);

function getInputParametersRows(data: ICO2InputParameters) {
  const co2FormValuesBefore = requireValue(data.co2_emissions_before, 'form_values').form_values;
  const co2FormValuesPotential = requireValue(
    data.co2_emissions_potential,
    'form_values',
  ).form_values;
  const co2FormValuesAfter = data?.co2_emissions_after?.form_values || {};
  const keys = Object.keys(omit(co2FormValuesBefore, '__typename'));

  const format = (value: unknown) => {
    if (!value) return '';
    return isArray(value) ? value.join(', ') : String(value);
  };

  const rows = keys.map((key) => ({
    key,
    optionName: `debug_mode.co2_values.form_values.${key}`,
    actualScenarioInput: format(valueOf(co2FormValuesBefore, key)),
    potentialScenarioInput: format(valueOf(co2FormValuesPotential, key)),
    plannedScenarioInput: format(valueOf(co2FormValuesAfter, key)),
  }));
  return rows;
}

const inputParametersColumns = [
  {
    title: 'debug_mode.co2_values.columns.option_name',
    key: 'optionName',
    dataIndex: 'optionName',
    width: '25%',
  },
  {
    title: 'debug_mode.co2_values.columns.actual_scenario_input',
    key: 'actualScenarioInput',
    dataIndex: 'actualScenarioInput',
    width: '25%',
  },
  {
    title: 'debug_mode.co2_values.columns.potential_scenario_input',
    key: 'potentialScenarioInput',
    dataIndex: 'potentialScenarioInput',
    width: '25%',
  },
  {
    title: 'debug_mode.co2_values.columns.planned_scenario_input',
    key: 'plannedScenarioInput',
    dataIndex: 'plannedScenarioInput',
    width: '25%',
  },
];

const CO2InputParametersTable = ({ values, t }: IParametersTableProps<ICO2InputParameters>) => {
  const localizedColumns = useMemo(
    () =>
      inputParametersColumns.map(({ title, ...rest }) => ({ ...rest, title: t({ id: title }) })),
    [t],
  );

  const rows = useMemo(() => getInputParametersRows(values), [values]);
  const localizedRows = useMemo(
    () => rows.map(({ optionName, ...rest }) => ({ ...rest, optionName: t({ id: optionName }) })),
    [rows, t],
  );

  return (
    <Table
      className="mb-4"
      style={{ width: '100%' }}
      scroll={{ x: '100%' }}
      direction="ltr"
      columns={localizedColumns}
      data={localizedRows}
      emptyText="No data"
    />
  );
};

const outputParametersColumns = [
  {
    title: 'debug_mode.co2_values.columns.option_name',
    key: 'optionName',
    dataIndex: 'optionName',
    width: '25%',
  },
  {
    title: 'debug_mode.co2_values.columns.actual_scenario_output',
    key: 'actualScenarioOutput',
    dataIndex: 'actualScenarioOutput',
    width: '25%',
  },
  {
    title: 'debug_mode.co2_values.columns.actual_scenario_output',
    key: 'potentialScenarioOutput',
    dataIndex: 'potentialScenarioOutput',
    width: '25%',
  },
  {
    title: 'debug_mode.co2_values.columns.actual_scenario_output',
    key: 'plannedScenarioOutput',
    dataIndex: 'plannedScenarioOutput',
    width: '25%',
  },
];

/** A result value: a number, or a value calculated by the server that the user may override. */
const isCalculatedValue = (value: unknown): value is { calculated_value: number } =>
  typeof value === 'object' && value !== null && 'calculated_value' in value;

function getOutputParametersRows(data: ICO2OutputParameters) {
  const co2ResultBefore = requireValue(data.co2_emissions_before, 'result').result;
  const co2PotentialResult = requireValue(data.co2_emissions_potential, 'result').result;
  const co2ResultAfter = get(data, 'co2_emissions_after.result', {});
  const valueKeys = Object.keys(omit(co2ResultBefore, '__typename'));

  const format = (value: unknown) => {
    if (isNull(value)) return '';
    if (isCalculatedValue(value)) {
      return formatValue(value.calculated_value);
    }
    // Number(): formatValue coerces the same way.
    // Bug: without a planned scenario every planned output cell shows "NaN" (undefined).
    return formatValue(Number(value));
  };

  const rows = valueKeys.map((key) => ({
    key,
    optionName: `debug_mode.co2_values.result.${key}`,
    actualScenarioOutput: format(valueOf(co2ResultBefore, key)),
    potentialScenarioOutput: format(valueOf(co2PotentialResult, key)),
    plannedScenarioOutput: format(valueOf(co2ResultAfter, key)),
  }));
  return rows;
}

const CO2OutputParametersTable = ({ t, values }: IParametersTableProps<ICO2OutputParameters>) => {
  const localizedColumns = useMemo(
    () =>
      outputParametersColumns.map(({ title, ...rest }) => ({ ...rest, title: t({ id: title }) })),
    [t],
  );
  const rows = useMemo(() => getOutputParametersRows(values), [values]);
  const localizedRows = useMemo(
    () => rows.map(({ optionName, ...rest }) => ({ ...rest, optionName: t({ id: optionName }) })),
    [rows, t],
  );

  return (
    <Table
      style={{ width: '100%' }}
      scroll={{ x: '100%' }}
      direction="ltr"
      columns={localizedColumns}
      data={localizedRows}
      emptyText="No data"
    />
  );
};

export default () => {
  // The route (`/secure/project/:id/debug-mode`) always has the id.
  const { id: projectId } = useParams<{ id: string }>();
  const { formatMessage: t } = useIntl();
  const [co2Calculations, loading] = useGetCO2Calculations(projectId);

  const co2InputParameters = pick(co2Calculations, [
    'co2_emissions_before.form_values',
    'co2_emissions_potential.form_values',
    'co2_emissions_after.form_values',
  ]);

  const co2OutputParameters = pick(co2Calculations, [
    'co2_emissions_before.result',
    'co2_emissions_potential.result',
    'co2_emissions_after.result',
  ]);

  if (loading) return <LoadingOverlay />;

  if (!co2Calculations) return null;

  return (
    <>
      <CO2InputParametersTable values={co2InputParameters} t={t} />
      <CO2OutputParametersTable values={co2OutputParameters} t={t} />
    </>
  );
};
